import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Col from 'react-bootstrap/esm/Col';
import Row from 'react-bootstrap/esm/Row'; 
import ProgressBar from 'react-bootstrap/ProgressBar';
import '../pages/card/card.css'

const IMAGE_TYPE_OPTIONS = [
    { value: 'grader_front_url', label: 'Grader Front' },
    { value: 'grader_back_url', label: 'Grader Back' },
    { value: 'scan_front_url', label: 'Scan Front' },
    { value: 'scan_back_url', label: 'Scan Back' },
    { value: 'alientrader_front_url', label: 'Alien Trader Front' },
    { value: 'alientrader_back_url', label: 'Alien Trader Back' },
    { value: 'misc_image_url', label: 'Miscellaneous 1' },
    { value: 'misc_image2_url', label: 'Miscellaneous 2' },
    { value: 'misc_image3_url', label: 'Miscellaneous 3' },
];

const MULTI_UPLOAD_FIELDS = new Set([
    'misc_image_url',
    'misc_image2_url',
    'misc_image3_url',
]);

const ImageUploadComponent = (props) => {
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const filesRef = useRef([]);

    const getDuplicatePrimaryAssignments = (entries) => {
        const assignmentCounts = entries.reduce((counts, entry) => {
            if (!MULTI_UPLOAD_FIELDS.has(entry.fieldName)) {
                counts[entry.fieldName] = (counts[entry.fieldName] || 0) + 1;
            }
            return counts;
        }, {});

        return Object.entries(assignmentCounts)
            .filter(([, count]) => count > 1)
            .map(([fieldName]) => {
                const option = IMAGE_TYPE_OPTIONS.find((item) => item.value === fieldName);
                return option ? option.label : fieldName;
            });
    };

    useEffect(() => {
        filesRef.current = files;
    }, [files]);

    useEffect(() => {
        const duplicateTargets = getDuplicatePrimaryAssignments(files);

        if (duplicateTargets.length > 0) {
            setWarningMessage(`Only one upload is allowed for ${duplicateTargets.join(', ')}. Use Miscellaneous for multiple images.`);
            return;
        }

        setWarningMessage('');
    }, [files]);

    useEffect(() => {
        return () => {
            filesRef.current.forEach((entry) => {
                if (entry.previewUrl) {
                    URL.revokeObjectURL(entry.previewUrl);
                }
            });
        };
    }, []);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);

        setFiles((prevFiles) => [
            ...prevFiles,
            ...selectedFiles.map((file, index) => ({
                id: `${file.name}-${file.lastModified}-${prevFiles.length + index}`,
                file,
                fieldName: 'grader_front_url',
                previewUrl: URL.createObjectURL(file),
            })),
        ]);

        e.target.value = '';
    };

    const handleFieldChange = (fileId, fieldName) => {
        setFiles((prevFiles) =>
            prevFiles.map((entry) =>
                entry.id === fileId ? { ...entry, fieldName } : entry
            )
        );
    };

    const handleRemoveFile = (fileId) => {
        setFiles((prevFiles) => {
            const fileToRemove = prevFiles.find((entry) => entry.id === fileId);
            if (fileToRemove?.previewUrl) {
                URL.revokeObjectURL(fileToRemove.previewUrl);
            }

            return prevFiles.filter((entry) => entry.id !== fileId);
        });
    };

    const handleUpload = async () => {
        if (!files.length) {
            return;
        }

        const duplicateTargets = getDuplicatePrimaryAssignments(files);
        if (duplicateTargets.length > 0) {
            setWarningMessage(`Only one upload is allowed for ${duplicateTargets.join(', ')}. Use Miscellaneous for multiple images.`);
            return;
        }

        const formData = new FormData();
        setUploading(true);
        setUploadProgress(0);

        files.forEach(({ fieldName, file }) => {
            formData.append(fieldName, file);
        });

        try {
            const response = await axios.post(`https://api.alientrader.cards/card/upload/${props.cardId}`, formData, {
                onUploadProgress: (progressEvent) => {
                    let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });
            console.log(response.data);
            props.onUrlsReceived(response.data); 
            files.forEach((entry) => {
                if (entry.previewUrl) {
                    URL.revokeObjectURL(entry.previewUrl);
                }
            });
            setFiles([]);
            setWarningMessage('');
        } catch (error) {
            console.error("Error uploading files:", error);
            setWarningMessage(
                error?.response?.data?.message ||
                error?.response?.data ||
                'Unable to upload these images.'
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <Row className='justify-content-center'>
            <Row className='justify-content-center'>
                <button onClick={handleUpload} className='uploadButton'>Upload</button>
            </Row>
            <Row className='uploadProgressRow'>
                {uploading && <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />}
            </Row>
            <Row className='justify-content-center'>
                <Col md={10} lg={8} className='custom-card-box shadow-sm'>
                    <Row>
                        <div className='custom-info-header'>Upload Images</div>
                    </Row>
                    <Row className='card-images'>
                        <Col>
                            <Row className='justify-content-center' style={{ padding: '12px' }}>
                                <input type="file" multiple onChange={handleFileChange} />
                            </Row>
                            <Row style={{ padding: '12px' }}>
                                <div className='custom-info-misc' style={{ marginBottom: '12px' }}>
                                    Select multiple files, then choose what each image should update before uploading.
                                </div>
                                {warningMessage && (
                                    <div
                                        className='custom-info-misc'
                                        style={{
                                            width: '100%',
                                            marginBottom: '12px',
                                            color: '#8a2323',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {warningMessage}
                                    </div>
                                )}
                                {files.length === 0 ? (
                                    <div className='custom-info-misc'>No files selected yet.</div>
                                ) : (
                                    files.map((entry) => (
                                        <div
                                            key={entry.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                marginBottom: '10px',
                                                flexWrap: 'wrap',
                                                width: '100%',
                                            }}
                                        >
                                            <img
                                                src={entry.previewUrl}
                                                alt={entry.file.name}
                                                style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '1px solid rgba(0,0,0,0.12)',
                                                }}
                                            />
                                            <div style={{ minWidth: '220px', flex: '1 1 220px' }}>
                                                {entry.file.name}
                                            </div>
                                            <select
                                                value={entry.fieldName}
                                                onChange={(e) => handleFieldChange(entry.id, e.target.value)}
                                                className='edit-input'
                                                style={{ maxWidth: '240px' }}
                                            >
                                                {IMAGE_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className='uploadButton'
                                                onClick={() => handleRemoveFile(entry.id)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))
                                )}
                            </Row>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className='uploadProgressRow'>
                {uploading && <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />}
            </Row>
            <Row className='justify-content-center'>
                <button onClick={handleUpload} className='uploadButton'>Upload</button>
            </Row>
        </Row>
    );
};

export default ImageUploadComponent;
